import { useLoaderData } from 'react-router';
import React from 'react';
import type { ItemShippingType } from '@modules/ecommerce/context/ItemShippingContext';
import {
    makeShippingOption,
    useItemShipping,
} from '@modules/ecommerce/context/ItemShippingContext';
import { useVariantSelectionState } from '../context/VariantSelectionContext';
import type { ProductPageLoaderData } from '~/routes/_app.product.$urlSegment';
import { useLatest } from 'ahooks';
import type { ShippingMethodType } from '~/modules/ecommerce/context/VariantShippingMethodsContext';
import { useVariantShippingMethods } from '~/modules/ecommerce/context/VariantShippingMethodsContext';

const shippingMethodsReady = (methods: ShippingMethodType[]): boolean => {
    return (
        methods.length === 0 ||
        methods.filter((i) => i.ShippingCost === null).length === 0
    );
};

const getDefaultShippingMethods = (
    isSample: boolean,
    product: ProductPageLoaderData['product'],
    shippingMethods: ShippingMethodType[] = [],
): ItemShippingType | null => {
    if (!shippingMethodsReady(shippingMethods)) {
        return null;
    }

    if (shippingMethods.length) {
        const sorted = shippingMethods
            .filter((i) => (i.ShippingCost ?? null) !== null)
            .sort((a, b) => {
                if (b.QuotedSeparately) {
                    return -1;
                }
                return (
                    (a.ShippingCost?.Amount ?? -1) -
                    (b.ShippingCost?.Amount ?? -1)
                );
            });
        const first = sorted[0] ?? null;
        if (first) {
            return makeShippingOption(
                'delivery',
                first.ID,
                first.ShippingProvider ?? undefined,
            );
        }
    }

    const pickupLocation = product.Professional?.PickupLocations?.[0] ?? null;
    if (pickupLocation) {
        return makeShippingOption('clickAndCollect', pickupLocation.ID);
    }

    return null;
};

export const useVariantShipping = (isSample = false) => {
    const { product, shoppingCartSummary } =
        useLoaderData<ProductPageLoaderData>();
    const { selectedVariant } = useVariantSelectionState();
    const variantShippingMethods = useVariantShippingMethods();
    const shippingMethods = isSample
        ? variantShippingMethods.sampleShippingMethods
        : variantShippingMethods.shippingMethods;
    const pickupLocationsRef = useLatest(product.Professional.PickupLocations);

    const state = useItemShipping();
    const prevVariantID = React.useRef(selectedVariant?.ID);
    const preferredRegionID = shoppingCartSummary.Region?.ID ?? 0;

    const defaultMethod = React.useMemo(() => {
        return getDefaultShippingMethods(isSample, product, shippingMethods);
    }, [isSample, product, shippingMethods]);

    React.useEffect(() => {
        const {
            variantShipping,
            setVariantShipping,
            sampleShipping,
            setSampleShipping,
        } = state;
        if (variantShippingMethods.isLoading) {
            return;
        }

        if (!shippingMethodsReady(shippingMethods)) {
            return;
        }

        const shipping = isSample ? sampleShipping : variantShipping;
        const setShipping = isSample ? setSampleShipping : setVariantShipping;
        const pickupLocations = pickupLocationsRef.current;

        if (!selectedVariant) {
            if (variantShipping) setVariantShipping(null);
            if (sampleShipping) setSampleShipping(null);
            return;
        }

        if (prevVariantID.current !== selectedVariant.ID) {
            prevVariantID.current = selectedVariant.ID;
            if (shipping?.type === 'delivery') {
                if (variantShipping) setVariantShipping(null);
                if (sampleShipping) setSampleShipping(null);
            }
            return;
        }

        if (shippingMethods.length && !defaultMethod) {
            return;
        }

        if (!shipping) {
            setShipping(defaultMethod);
            return;
        }

        // Check the shipping option is valid
        if (
            shipping.type === 'delivery' &&
            !shippingMethods.find((i) => i.ID === shipping.ID)
        ) {
            if (defaultMethod) {
                setShipping(defaultMethod);
                return;
            }

            const pickupLocation = pickupLocations.find(
                (l) => l.RegionType?.ID === preferredRegionID,
            );
            if (pickupLocation) {
                setShipping(
                    makeShippingOption('clickAndCollect', pickupLocation.ID),
                );
            } else {
                setShipping(null);
            }
            return;
        }
    }, [
        state,
        selectedVariant,
        preferredRegionID,
        product,
        isSample,
        shippingMethods,
        variantShippingMethods.isLoading,
        defaultMethod,
        pickupLocationsRef,
    ]);

    return React.useMemo(() => {
        const itemShippingType =
            (isSample ? state.sampleShipping : state.variantShipping) ??
            defaultMethod;
        return {
            shippingMethods,
            shipping: itemShippingType,
            setShipping: isSample
                ? state.setSampleShipping
                : state.setVariantShipping,
            hasClickAndCollect: pickupLocationsRef.current.length > 0,
            hasShippingType: !!itemShippingType,
        };
    }, [state, isSample, defaultMethod, shippingMethods, pickupLocationsRef]);
};
